import type { ApprovalEntry } from './types';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, XCircle, Rocket, RotateCcw } from 'lucide-react';
import { useState } from 'react';

interface Props {
  approval: ApprovalEntry;
  currentUserId: string;
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string, note: string | undefined) => Promise<void>;
  onGoLive: (id: string) => Promise<void>;
  onResubmit: (id: string) => Promise<void>;
}

export function ApprovalWorkflow({ approval, currentUserId, onApprove, onReject, onGoLive, onResubmit }: Props) {
  const [rejectNote, setRejectNote] = useState('');
  const [loading, setLoading] = useState(false);
  const isSameUser = approval.submitterId === currentUserId;

  const handle = async (fn: () => Promise<void>) => {
    setLoading(true);
    try { await fn(); }
    finally { setLoading(false); }
  };

  if (approval.status === 'InReview') {
    return (
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="default"
          disabled={isSameUser || loading}
          title={isSameUser ? '4-Augen-Prinzip: Sie können eigene Anfragen nicht genehmigen' : undefined}
          onClick={() => handle(() => onApprove(approval.id))}
        >
          <CheckCircle className="mr-1 h-4 w-4" />
          Genehmigen
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="destructive" disabled={isSameUser || loading}
              title={isSameUser ? '4-Augen-Prinzip: Sie können eigene Anfragen nicht ablehnen' : undefined}>
              <XCircle className="mr-1 h-4 w-4" />
              Ablehnen
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Genehmigung ablehnen</AlertDialogTitle>
              <AlertDialogDescription>
                {approval.targetLabel}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea
              placeholder="Begründung (optional)"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handle(() => onReject(approval.id, rejectNote || undefined))}
              >
                Ablehnen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (approval.status === 'Approved') {
    return (
      <Button size="sm" disabled={loading} onClick={() => handle(() => onGoLive(approval.id))}>
        <Rocket className="mr-1 h-4 w-4" />
        Live schalten
      </Button>
    );
  }

  if (approval.status === 'Rejected') {
    return (
      <Button size="sm" variant="outline" disabled={loading} onClick={() => handle(() => onResubmit(approval.id))}>
        <RotateCcw className="mr-1 h-4 w-4" />
        Erneut einreichen
      </Button>
    );
  }

  return null;
}
